/**
 * Phase F4 — Admin Fraud Center
 * ------------------------------
 * Surfaces Phase 10 fraud flags engine.
 *
 * Endpoints used:
 *   GET  /api/admin/fraud-flags?status=…&severity=…
 *   POST /api/admin/fraud-flags/{flag_id}/transition (body: {target, notes?})
 *   POST /api/admin/fraud-flags/{flag_id}/assign     (body: {admin_id})
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldAlert, AlertTriangle, Eye, CheckCircle, XCircle, X,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'OPEN', label: 'Active', Icon: AlertTriangle, color: theme.colors.status.error },
  { key: 'INVESTIGATING', label: 'Investigating', Icon: Eye, color: theme.colors.status.warning },
  { key: 'CONFIRMED', label: 'Confirmed', Icon: CheckCircle, color: theme.colors.status.success },
  { key: 'DISMISSED', label: 'Dismissed', Icon: XCircle, color: theme.colors.text.muted },
];

const ALLOWED_NEXT = {
  OPEN: ['INVESTIGATING', 'CONFIRMED', 'DISMISSED'],
  INVESTIGATING: ['CONFIRMED', 'DISMISSED', 'OPEN'],
  CONFIRMED: [],
  DISMISSED: [],
};

const SEVERITY_COLOR = {
  LOW: theme.colors.text.muted,
  MEDIUM: theme.colors.status.warning,
  HIGH: theme.colors.accent.salmon,
  CRITICAL: theme.colors.status.error,
};

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm,
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminFraudCenter({ user }) {
  const [activeTab, setActiveTab] = useState('OPEN');
  const [severityFilter, setSeverityFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: activeTab });
      if (severityFilter) params.set('severity', severityFilter);
      const [all, filtered] = await Promise.all([
        axios.get(`${API}/admin/fraud-flags`, auth),
        axios.get(`${API}/admin/fraud-flags?${params.toString()}`, auth),
      ]);
      setAllRows(all.data?.fraud_flags || []);
      setRows(filtered.data?.fraud_flags || []);
    } catch (e) {
      toast.error('Failed to load fraud flags');
    } finally { setLoading(false); }
  }, [activeTab, severityFilter, auth]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = {};
    TABS.forEach((t) => { c[t.key] = 0; });
    allRows.forEach((f) => { if (c[f.status] !== undefined) c[f.status] += 1; });
    return c;
  }, [allRows]);

  const transition = async (id, target) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/fraud-flags/${id}/transition`, { target, notes: notes || null }, auth);
      toast.success(`Moved to ${target}`);
      setNotes('');
      setDrawer(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transition failed');
    } finally { setBusy(false); }
  };

  const assignToMe = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/fraud-flags/${id}/assign`, { admin_id: user?.id }, auth);
      toast.success('Assigned to you');
      await load();
    } catch (e) {
      toast.error('Assign failed');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-fraud-center-page" style={{ padding: 24, maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <ShieldAlert size={26} color={theme.colors.status.error} />
          Fraud Center
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Active fraud flags — assign, investigate, confirm or dismiss.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }} data-testid="fraud-tabs">
        {TABS.map((t) => {
          const Icon = t.Icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`fraud-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: theme.radius.cta,
                border: `1px solid ${active ? t.color : theme.colors.borders}`,
                background: active ? `${t.color}15` : theme.colors.bg.card,
                color: active ? t.color : theme.colors.text.secondary,
                cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              <Icon size={16} /> {t.label}
              <span style={{
                marginLeft: 4, background: active ? t.color : theme.colors.bg.tertiary,
                color: active ? '#fff' : theme.colors.text.secondary,
                padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              }}>{counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
        <span style={{ fontSize: 12, color: theme.colors.text.muted, fontWeight: 600 }}>Severity</span>
        <select
          data-testid="fraud-severity-filter"
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          style={{
            padding: '6px 10px', borderRadius: theme.radius.sm,
            border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
            color: theme.colors.text.primary, fontSize: 12,
          }}
        >
          <option value="">All</option>
          <option value="LOW">LOW</option>
          <option value="MEDIUM">MEDIUM</option>
          <option value="HIGH">HIGH</option>
          <option value="CRITICAL">CRITICAL</option>
        </select>
      </div>

      {loading ? (
        <div data-testid="fraud-loading" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.muted }}>
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="fraud-empty" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.secondary }}>
          No fraud flags in this bucket.
        </div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: theme.colors.bg.secondary }}>
              <tr>
                {['Flag #', 'Type', 'Severity', 'Status', 'Subject', 'Assigned', 'Notes', ''].map((h) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '10px 12px',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: theme.colors.text.muted, fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => {
                const sevColor = SEVERITY_COLOR[f.severity] || theme.colors.text.muted;
                return (
                  <tr key={f.id} data-testid={`fraud-row-${f.id}`} style={{ borderTop: `1px solid ${theme.colors.borders}` }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700 }}>{f.flag_number || f.id?.slice(0, 8)}</td>
                    <td style={{ padding: '10px 12px' }}>{f.fraud_type || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 999,
                        background: `${sevColor}1A`, color: sevColor,
                        fontSize: 11, fontWeight: 700,
                      }}>{f.severity}</span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 999,
                        background: `${theme.colors.primary}15`, color: theme.colors.primary,
                        fontSize: 11, fontWeight: 700,
                      }}>{f.status}</span>
                    </td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 11 }}>
                      {(f.subject_user_id || f.subject_portfolio_id || '').slice(0, 10) || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 11, color: theme.colors.text.secondary }}>
                      {f.assigned_admin_id ? f.assigned_admin_id.slice(0, 8) + '…' : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: theme.colors.text.muted, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.notes}>
                      {f.notes || '—'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {!f.assigned_admin_id && (
                          <button
                            data-testid={`fraud-assign-${f.id}`} disabled={busy}
                            onClick={() => assignToMe(f.id)}
                            style={{
                              padding: '5px 10px', borderRadius: theme.radius.sm,
                              border: `1px solid ${theme.colors.borders}`,
                              background: theme.colors.bg.card, cursor: 'pointer',
                              fontSize: 11, fontWeight: 700, color: theme.colors.primary,
                            }}>Assign to me</button>
                        )}
                        <button
                          data-testid={`fraud-open-${f.id}`}
                          onClick={() => { setDrawer(f); setNotes(''); }}
                          style={{
                            padding: '5px 10px', borderRadius: theme.radius.sm,
                            border: 'none', background: theme.colors.primary, color: '#fff',
                            cursor: 'pointer', fontSize: 11, fontWeight: 700,
                          }}>Open</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {drawer && (
        <div data-testid="fraud-drawer" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', justifyContent: 'flex-end', zIndex: 100,
        }} onClick={() => setDrawer(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: 'min(520px, 100%)', height: '100%', background: theme.colors.bg.card,
            padding: 24, overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 18 }}>{drawer.flag_number || drawer.id?.slice(0, 8)}</div>
              <button onClick={() => setDrawer(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ marginBottom: 12, fontSize: 13 }}>
              {[
                ['Type', drawer.fraud_type], ['Severity', drawer.severity], ['Status', drawer.status],
                ['Subject User', drawer.subject_user_id || '—'],
                ['Subject Portfolio', drawer.subject_portfolio_id || '—'],
                ['Created', fmtDate(drawer.created_at)],
                ['Assigned', drawer.assigned_admin_id || '—'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${theme.colors.borders}` }}>
                  <span style={{ color: theme.colors.text.muted }}>{k}</span>
                  <span style={{ fontWeight: 600 }}>{v || '—'}</span>
                </div>
              ))}
              {drawer.notes && (
                <div style={{
                  padding: 10, background: theme.colors.bg.secondary, marginTop: 10,
                  borderRadius: theme.radius.sm, fontSize: 12, color: theme.colors.text.secondary,
                }}>{drawer.notes}</div>
              )}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text.secondary, marginBottom: 6, display: 'block' }}>
                Decision Notes
              </label>
              <textarea
                data-testid="fraud-notes"
                value={notes} onChange={(e) => setNotes(e.target.value)}
                style={{
                  width: '100%', minHeight: 70, padding: 10,
                  border: `1px solid ${theme.colors.borders}`,
                  borderRadius: theme.radius.sm, fontFamily: 'inherit', fontSize: 13,
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(ALLOWED_NEXT[drawer.status] || []).map((target) => (
                <button
                  key={target}
                  data-testid={`fraud-transition-${target}`}
                  disabled={busy}
                  onClick={() => transition(drawer.id, target)}
                  style={{
                    padding: '7px 12px', borderRadius: theme.radius.sm, cursor: 'pointer',
                    border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
                    fontSize: 12, fontWeight: 700, color: theme.colors.text.primary,
                  }}
                >
                  → {target}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
